import { useState, useCallback, useLayoutEffect } from 'react';
import { CloseIcon, toaster } from '@halo/ui-chat-react';
import { Box, Image } from '@chakra-ui/react';
import { Dialog } from '@halo/ui-react';
import {
  isSessionCategory,
  LuckyBoxGrabStatus,
  MessageStatus,
  MsgPayloadType,
  SessionCategoryPrefix,
  type SessionStoreInstance,
} from '@halo/chat-sdk-react';
import bs58 from 'bs58';
import { walletTools } from '@halo/wallet-app';
import dayjs from 'dayjs';
import { useStore } from 'zustand';
import luckyBoxHeaderImg from '@/assets/images/luckyBox/luckyBoxHeader.png';
import { useWalletStore } from '@/wallet';
import { useSettingsStore } from '@/store/settings/store';
import { LuckyTabs } from './LuckyTabs';
import type { LuckyFormValues } from './LuckyTabs/LuckyForm';
import { base64ToUint8Array, uint8ArrayToBase64 } from './LuckyTabs/helpers';

interface LuckyDialogProps {
  sessionStoreInstance: SessionStoreInstance;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
}
export function LuckyDialog({ isOpen, setIsOpen, sessionStoreInstance }: LuckyDialogProps) {
  const { t } = useTranslation();
  const sessionStore = useStore(sessionStoreInstance);
  const [open, setOpen] = useState(isOpen);
  const { client, updateMessages } = sessionStore;
  const { signRawTransaction, signTransaction } = useWalletStore();
  const isGroup = useMemo(() => {
    return isSessionCategory(sessionStore?.session?.chat_id, SessionCategoryPrefix.Group);
  }, [sessionStore]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setIsOpen?.(false);
  }, [setIsOpen]);

  const handleConfirm = useCallback(
    async (luckyInfo: LuckyFormValues & Record<string, any>) => {
      const {
        chat_id,
        account,
        box_type,
        message: box_desc = '',
        box_icon,
        coin: { isMain, symbol, contract, chainLogo, logo, chainType, chainId, cmc_id },
        mainCoin: { cmc_id: mainCmcId, symbol: mainSymbol },
        totalAmount,
        quantity,
        sendTo,
        gasFeeInfo,
      } = luckyInfo;

      const curWallet = account.seriesWallets[chainType];
      const params = {
        // nonce?: string;
        main_cmc_id: mainCmcId,
        cmc_id: isMain ? undefined : cmc_id,
        chain_type: chainType,
        chain_id: chainId.toString(),
        sender: curWallet?.walletAddress,
        chat_id,
        box_type: Number(box_type),
        box_desc,
        box_icon,
        asset_mint: isMain ? symbol : contract,
        token_mint: isMain ? '' : symbol,
        total_amount: totalAmount,
        box_count: Number(quantity),
        per_amount: box_type === '1' ? totalAmount : '',
        deadline: dayjs().add(useSettingsStore.getState().luckyBoxDeadline, 'ms').valueOf(),
      };
      const data = await client?.Api.luckyBox.getLuckyBoxPresign(params);
      const { data: preSignRes } = data ?? {};
      if (!preSignRes?.content) {
        toaster.error({ title: t('tips.failedPreSignature') });
        return;
      }

      const bs58Msg = base64ToUint8Array(preSignRes?.content);
      const messageStr = bs58.encode(bs58Msg);

      
      const signParams = {
        method: 'signTransaction',
        transaction: messageStr,
        chainType: chainType,
        accountId: account.id,
        businessParams: {
          mainCoinSymbol: mainSymbol,
          sender: curWallet?.walletAddress,
          contract: '',
          amountChanges: [{
            change: `-${totalAmount}`,
            symbol,
            icon: logo
          }],
          gasInfo: {
            txFee: gasFeeInfo.oper_fee_claim_lamports_str,
            isEip1559: false
          }
        }
      }

      const signInfo = await signTransaction(signParams);

      // const signInfo = await signRawTransaction(
      //   {
      //     chainType: chainType,
      //     accountId: account.id,
      //     transaction: messageStr,
      //   },
      //   {
      //     businessType: 'luckyBox',
      //     businessParams: {
      //       accountId: account.id,
      //       chainType: chainType,
      //       symbol,
      //       mainSymbol,
      //       boxType: box_type,
      //       quantity: Number(quantity),
      //       amount: totalAmount,
      //       coverType: '1',
      //       grabStatus: LuckyBoxGrabStatus.unClaim,
      //       boxDesc: box_desc,
      //       packetNo: preSignRes?.id || '',
      //       deadline: params.deadline,
      //       sendTo,
      //       chainIcon: chainLogo,
      //       tokenIcon: logo,
      //       gasFeeInfo,
      //     },
      //   }
      // );

      if (!signInfo?.signature) {
        toaster.error({ title: t('tips.failedToSignature') });
        return;
      }

      walletTools.hideWalletApp();

      const payloadInfo = {
        chainType: chainType,
        symbol,
        boxType: box_type,
        quantity: Number(quantity),
        amount: totalAmount,
        coverType: '1',
        grabStatus: LuckyBoxGrabStatus.unClaim,
        boxDesc: box_desc,
        packetNo: preSignRes?.id,
        deadline: params.deadline,
        sendTo,
        chainIcon: chainLogo,
        tokenIcon: logo,
      };

      const payload = JSON.stringify(payloadInfo);
      const cliMessageId = await client?.Api.messages.saveLocalMessage(
        chat_id,
        payload,
        MsgPayloadType.LUCKY_BOX
      );

      const signatureArray = bs58.decode(signInfo?.signature);
      const base64Str = uint8ArrayToBase64(signatureArray);
      const creatData = await client?.Api.luckyBox
        .creationLuckyBox({
          id: preSignRes.id,
          pda: preSignRes.pda,
          nonce: preSignRes.nonce,
          content: base64Str,
          cli_msg_id: cliMessageId!,
        })
        .catch((err) => {
          if (err) {
            client?.Api.messages.editLocalMessage(
              chat_id,
              cliMessageId!,
              JSON.stringify({ ...payloadInfo, grabStatus: LuckyBoxGrabStatus.expired })
            );
            updateMessages?.([
              {
                cli_msg_id: cliMessageId!,
                payload: {
                  grabStatus: LuckyBoxGrabStatus.expired,
                },
              },
            ]);
          }
        });
      const { data: creationRes } = creatData ?? {};
      if (creationRes?.box_status === 3) {
        client?.Api.messages.sendLocalMessage(chat_id, cliMessageId!);
        setOpen(false);
        setIsOpen?.(false);
      } else {
        toaster.error({
          title: t('tips.failedToCreateLuckyBox'),
        });
        await client?.Api.messages.editLocalMessage(
          chat_id,
          cliMessageId!,
          JSON.stringify({
            ...payloadInfo,
            boxStatus: 1,
            grabStatus: LuckyBoxGrabStatus.expired,
          })
        );
        await client?.Api.messages.canCelSendLocalMessage(chat_id, cliMessageId!);
        updateMessages?.([
          {
            cli_msg_id: cliMessageId!,
            payload: {
              boxStatus: 1,
              grabStatus: LuckyBoxGrabStatus.expired,
            },
            status: MessageStatus.FailedSent,
          },
        ]);
      }
    },
    [client, signRawTransaction, updateMessages, setIsOpen, t]
  );

  useLayoutEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        setIsOpen?.(details.open);
      }}
      lazyMount
      unmountOnExit
      restoreFocus={false}
      trapFocus={false}
      closeOnInteractOutside={false}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner closeOnInteractOutside={false}>
        <Dialog.Content borderRadius="2xl" w="440px" maxW="100%" p="0" overflow="hidden">
          <Dialog.Header
            fontSize="md"
            fontWeight="semibold"
            justifyContent="space-between"
            py="2.5"
            px="0"
            minH="154px"
            pos="relative"
            css={{
              background:
                'linear-gradient(180deg, color(display-p3 0.7569 0.5686 0.8353 / 0.00) 21.34%, color(display-p3 1 1 1) 88.54%), linear-gradient(90deg, color(display-p3 0.9197 0.7791 0.3842 / 0.50) 37.57%, color(display-p3 0.8209 0.4569 0.9725 / 0.50) 100%)',
            }}
          >
            <Image src={luckyBoxHeaderImg} alt="img" w="full" />
            <Box
              pos="absolute"
              top="4"
              right="4"
              zIndex="1"
              justifyContent="flex-end"
              color="fg.muted"
              cursor="pointer"
              onClick={handleCancel}
            >
              <CloseIcon />
            </Box>
          </Dialog.Header>
          <Dialog.Body p="0">
            <LuckyTabs isGroup={isGroup} sessionStore={sessionStore} onConfirm={handleConfirm} />
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
